import React from 'react'

import { useMergedRefs } from '@base-ui/utils/useMergedRefs'
import { TextField, TextFieldProps } from '@mui/material'
import { Controller, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

export type InputFormFieldProps = TextFieldProps & {
  name: string
  hideLabel?: boolean
}

const TextFieldWithMergedRef = React.forwardRef<
  HTMLInputElement,
  TextFieldProps & { fieldRef: React.Ref<HTMLInputElement>; hideLabel?: boolean; name: string }
>(({ fieldRef, hideLabel, name, ...props }, ref) => {
  const mergedRef = useMergedRefs(fieldRef, ref)

  return (
    <TextField
      fullWidth
      size="small"
      {...props}
      inputRef={mergedRef}
      label={hideLabel ? '' : props.label}
      slotProps={{
        ...props.slotProps,
        htmlInput: {
          ...props.slotProps?.htmlInput,
          'data-testid': `inputfield-${name}`,
          name,
          ...(hideLabel && { 'aria-label': props.label?.toString() }),
        },
      }}
    />
  )
})

TextFieldWithMergedRef.displayName = 'TextFieldWithMergedRef'

const InputFormField = React.forwardRef<HTMLInputElement, InputFormFieldProps>(
  ({ name, helperText, hideLabel = false, ...props }, ref) => {
    const { t } = useTranslation()
    const { control } = useFormContext()

    return (
      <Controller
        control={control}
        name={name}
        render={({ field: { ref: fieldRef, ...field }, fieldState: { error } }) => (
          <TextFieldWithMergedRef
            {...field}
            {...props}
            ref={ref}
            fieldRef={fieldRef}
            hideLabel={hideLabel}
            name={name}
            error={!!error?.message}
            helperText={
              error?.message
                ? (t(error?.message, { label: props.label, defaultValue: '' }) as React.ReactNode)
                : helperText
            }
          />
        )}
      />
    )
  },
)

InputFormField.displayName = 'InputFormField'

export default InputFormField
